import { FloatingTree } from '@floating-ui/react';
import type { FC, ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { AuctionTilesTraffic } from 'src/apis/transport-auction/auction-tiles-traffic';
import { AuthSessionMonitor } from 'src/components/auth-session-monitor';
import { CookieCheck } from 'src/components/cookie-check';
import { ErrorBoundary } from 'src/components/feedback/error-boundary';
import { FatalErrorBoundary } from 'src/components/feedback/fatal-error-boundary';
import { AppShell } from 'src/components/layout/app-shell';
import { ScrollToTopButton } from 'src/components/layout/app-shell/scroll-to-top-button';
import { AppShellProvider } from 'src/components/layout/app-shell/use-app-shell-provider';
import { Container } from 'src/components/layout/container';
import { Footer } from 'src/components/layout/footer';
import { Header } from 'src/components/layout/header';
import { Layout, LayoutFooter, LayoutMain } from 'src/components/layout/layout';
import { SimplePageLayout } from 'src/components/layout/simple-page-layout';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { CookieBanner } from 'src/components/overlay/cookie-banner';
import { useWelcomeModal } from 'src/components/overlay/use-welcome-modal';
import { ScrollManagerProvider } from 'src/components/scroll-management';
import { ScrollToTop } from 'src/components/scroll-to-top';
import { PageSpinner } from 'src/components/spinner-container';
import { WizardsLiftetime } from 'src/components/wizard/use-wizard-store';
import { GlobalResourcesProvider } from 'src/global-resources-provider';
import { AccountsProvider } from 'src/hooks/use-accounts';
import {
  AxiosHookConfigProvider,
  TransformAxiosConfig,
} from 'src/hooks/use-axios';
import { BreakpointsProvider } from 'src/hooks/use-breakpoints';
import { CognitoUserProvider } from 'src/hooks/use-cognito-user';
import { DropManagerProvider } from 'src/hooks/use-drop-manager';
import { FullwidthProvider } from 'src/hooks/use-fullwidth';
import { HeaderHeightProvider } from 'src/hooks/use-header-height';
import { useShowHelpAnnotations } from 'src/hooks/use-help-annotations';
import { useInitialCleanup } from 'src/hooks/use-initial-cleanup';
import { IsPrintProvider } from 'src/hooks/use-is-print';
import { ModalContainerProvider } from 'src/hooks/use-modal';
import { PreviousLocationProvider } from 'src/hooks/use-previous-location';
import {
  RootToaster,
  sonnerConfig,
  useSonnerErrorDuration,
} from 'src/hooks/use-sonner';
import { useStorybook } from 'src/hooks/use-storybook';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { OurRoutes } from 'src/routes';
import { useCustomAxiosConfig } from 'src/utils/axios-config';

const DataTestidHighlighter = lazy(
  () => import('src/pages/developer-tools/data-testid-highlighter')
);
const HelpAnnotations = lazy(
  () => import('src/components/domain-specifics/help-annotations')
);
const WelcomeModal = lazy(() => import('src/components/overlay/welcome-modal'));
const ProductFruitsHelpWidget = lazy(
  () => import('src/components/product-fruits/help-widget')
);

export const App: FC = () => {
  useInitialCleanup();
  const { isStorybook } = useStorybook();
  const developerTools = useDeveloperTools();
  const showHelpAnnotations = useShowHelpAnnotations();
  const isNewDesignActive = useIsNewDesignActive();
  useSonnerErrorDuration();

  return (
    <GlobalProviders>
      <Suspense
        fallback={
          <SimplePageLayout>
            <PageSpinner />
          </SimplePageLayout>
        }
      >
        <FatalErrorBoundary>
          <ScrollToTop />

          <CookieCheck />

          <CognitoUserProvider>
            <AccountsProvider>
              <GlobalResourcesProvider>
                <ModalContainerProvider>
                  <AuthSessionMonitor />

                  <WizardsLiftetime>
                    <LayoutSelector isNewDesignActive={isNewDesignActive}>
                      <div className="animate-fade-in">
                        <ScrollManagerProvider>
                          <Suspense fallback={null}>
                            <ProductFruitsHelpWidget />
                          </Suspense>

                          <Suspense fallback={<PageSpinner />}>
                            <ErrorBoundary>
                              <AuctionTilesTraffic />

                              <CheckWelcomeModal />

                              <TransformAxiosConfig
                                transform={(config) => ({
                                  ...config,
                                  // every request that happens within the
                                  // routes will be flagged with this section
                                  requestSection: 'page-content',
                                })}
                              >
                                <OurRoutes />
                              </TransformAxiosConfig>
                            </ErrorBoundary>
                          </Suspense>
                        </ScrollManagerProvider>
                      </div>
                    </LayoutSelector>

                    {!isStorybook && (
                      <div className="fixed bottom-0 z-2 w-full">
                        <CookieBanner />
                      </div>
                    )}

                    {developerTools && (
                      <Suspense fallback={null}>
                        <DataTestidHighlighter />
                      </Suspense>
                    )}

                    {showHelpAnnotations && (
                      <Suspense fallback={null}>
                        <HelpAnnotations />
                      </Suspense>
                    )}
                  </WizardsLiftetime>
                </ModalContainerProvider>
              </GlobalResourcesProvider>
            </AccountsProvider>
          </CognitoUserProvider>
        </FatalErrorBoundary>
      </Suspense>
    </GlobalProviders>
  );
};

const GlobalProviders: FC<{ children: ReactNode }> = ({ children }) => {
  const axiosHookConfig = useCustomAxiosConfig();
  const isNewDesignActive = useIsNewDesignActive();
  return (
    <FloatingTree>
      <AppShellProvider>
        <AxiosHookConfigProvider value={axiosHookConfig}>
          <RootToaster
            {...(!isNewDesignActive && {
              ...sonnerConfig,
              offset: { top: '100px', right: '20px' },
            })}
          />

          <PreviousLocationProvider>
            <DropManagerProvider>
              <IsPrintProvider>
                <BreakpointsProvider>
                  <HeaderHeightProvider>
                    <FullwidthProvider>{children}</FullwidthProvider>
                  </HeaderHeightProvider>
                </BreakpointsProvider>
              </IsPrintProvider>
            </DropManagerProvider>
          </PreviousLocationProvider>
        </AxiosHookConfigProvider>
      </AppShellProvider>
    </FloatingTree>
  );
};

const LayoutSelector: FC<{
  isNewDesignActive: boolean;
  children: ReactNode;
}> = ({ isNewDesignActive, children }) => {
  if (isNewDesignActive) return <AppShell>{children}</AppShell>;

  return (
    <Layout>
      <Header />

      <AngularModalDimmer clipAngular>
        <LayoutMain data-testid="page-content">
          <Container>{children}</Container>
          <ScrollToTopButton />
        </LayoutMain>
      </AngularModalDimmer>

      <LayoutFooter data-testid="layout-footer">
        <Footer />
      </LayoutFooter>
    </Layout>
  );
};

const CheckWelcomeModal: FC = () => {
  const { shouldShowWelcomeModal } = useWelcomeModal();
  return shouldShowWelcomeModal ? <WelcomeModal /> : null;
};
